import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { HxBaseControl } from '../../base';
import { HxAlert } from '../alert/alert';
import { HxTreeNodeComponent } from './tree-node/tree-node';
import { HxTreeConfig, HxTreeNode } from './tree.types';

@Component({
  selector: 'hx-tree',
  standalone: true,
  imports: [CommonModule, HxTreeNodeComponent, HxAlert],
  templateUrl: './tree.html',
  styleUrls: ['./tree.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxTreeComponent),
      multi: true,
    },
  ],
  host: {
    '[style.--tree-max-height]': 'maxHeight()',
    // Color tokens are now managed via CSS only
    // ...existing code...
    '[class.hx-tree--no-lines]': '!showConnectorLines()',
  },
})
export class HxTreeComponent extends HxBaseControl {
  private readonly router = inject(Router, { optional: true });
  private isDisabled = false;
  private onChange: (value: HxTreeNode | string | null) => void = () => {};
  private onTouched: () => void = () => {};

  readonly collapsed = input<boolean>(false);
  readonly maxHeight = input<string>('');

  // --- Data & Config ---
  readonly nodes = input.required<HxTreeNode[]>();
  readonly config = input<HxTreeConfig>({ collapsible: true, levelSpacing: 24, showConnectorLines: true });
  readonly readonly = input<boolean>(false);
  readonly nodeTemplate = input<TemplateRef<any> | null>(null);

  // --- Active Node State Control ---
  readonly activeNodeIdInput = input<string | null>(null);

  // --- General Node Styling ---
  readonly nodeTextColor = input<string>('');
  readonly nodeBackgroundColor = input<string>('');
  readonly nodeIconColor = input<string>('');
  readonly nodeHoverBackgroundColor = input<string>('');

  // --- Toggles ---
  readonly showStatusIcons = input<boolean>(true);
  readonly showChildIcons = input<boolean>(true);

  // --- Active State Styling ---
  readonly activeChildBackgroundColor = input<string>('');
  readonly activeRootBackgroundColor = input<string>('');

  // --- Connector Line Colors ---
  readonly errorConnectorLineColor = input<string>('');
  readonly successConnectorLineColor = input<string>('');
  readonly defaultConnectorLineColor = input<string>('');
  readonly useUniformConnectorLineColor = input<boolean>(false);

  // --- Status Background Colors ---
  readonly errorBackgroundColor = input<string>('');
  readonly successBackgroundColor = input<string>('');
  readonly defaultBackgroundColor = input<string>('');
  readonly useUniformBackgroundColor = input<boolean>(false);

  // --- Outputs ---
  readonly selectionChange = output<HxTreeNode>();
  readonly nodeToggle = output<{ id: string; expanded: boolean }>();

  // --- State ---
  readonly expandedNodeIds = signal<Set<string>>(new Set());
  readonly activeNodeId = signal<string | null>(null);
  readonly focusedNodeId = signal<string | null>(null);

  readonly navigationType = computed(() => this.config().navigationType ?? 'section');
  readonly hasExplicitNavigationType = computed(() => !!this.config().navigationType);
  readonly showConnectorLines = computed(() => this.config().showConnectorLines ?? true);
  readonly showFragments = computed(() => this.config().showFragments ?? false);
  readonly useCustomTemplate = computed(() => this.config().useCustomTemplate && this.nodeTemplate() !== null);

  readonly missingRouteNodes = computed(() => {
    if (this.navigationType() !== 'route') return [] as HxTreeNode[];
    return this.collectNodesMissingRoute(this.nodes());
  });

  readonly missingSectionNodes = computed(() => {
    if (this.navigationType() !== 'section') return [] as HxTreeNode[];
    return this.collectNodesMissingSection(this.nodes());
  });

  readonly shouldShowRouteAlert = computed(
    () => this.navigationType() === 'route' && this.missingRouteNodes().length > 0
  );
  readonly shouldShowSectionAlert = computed(
    () => this.navigationType() === 'section' && this.missingSectionNodes().length > 0
  );

  readonly missingRouteLabels = computed(() => this.formatMissingLabels(this.missingRouteNodes()));
  readonly missingSectionLabels = computed(() => this.formatMissingLabels(this.missingSectionNodes()));

  constructor() {
    super();
    effect(() => {
      const nodes = this.nodes();
      const ids = new Set<string>();
      let initialActiveNode: HxTreeNode | null = null;
      let firstNodeId: string | null = null;

      const shouldExpandAll = !!this.config().defaultExpandAll;
      const fragment = this.showFragments() ? this.getCurrentFragment() : null;

      const traverse = (list: HxTreeNode[], parentIds: string[] = []) => {
        for (const n of list) {
          if (!firstNodeId) firstNodeId = n.id;

          const hasChildren = !!n.children?.length;
          if (n.expanded === true || (shouldExpandAll && hasChildren && n.expanded !== false)) {
            ids.add(n.id);
          }

          if (fragment && n.id === fragment) {
            initialActiveNode = n;
            parentIds.forEach((pid) => ids.add(pid));
          } else if (!fragment && n.active) {
            initialActiveNode = n;
            parentIds.forEach((pid) => ids.add(pid));
          }

          if (hasChildren) {
            traverse(n.children!, [...parentIds, n.id]);
          }
        }
      };

      traverse(nodes);

      this.expandedNodeIds.set(ids);

      if (initialActiveNode) {
        const active = initialActiveNode as HxTreeNode;
        this.activeNodeId.set(active.id);
        this.focusedNodeId.set(active.id);
      } else if (firstNodeId) {
        this.focusedNodeId.set(firstNodeId);
      }
    });

    // Sync external activeNodeId input with internal activeNodeId signal
    effect(() => {
      const externalActiveId = this.activeNodeIdInput();
      if (externalActiveId !== null) {
        this.activeNodeId.set(externalActiveId);
      }
    });
  }

  writeValue(value: HxTreeNode | string | null): void {
    if (!value) {
      this.activeNodeId.set(null);
      return;
    }

    const nodeId = typeof value === 'string' ? value : value.id;
    this.activeNodeId.set(nodeId);
    this.focusedNodeId.set(nodeId);
  }

  registerOnChange(fn: (value: HxTreeNode | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  handleNodeSelect(node: HxTreeNode) {
    if (node.disabled || this.readonly() || this.isDisabled) return;
    this.activeNodeId.set(node.id);
    this.focusedNodeId.set(node.id);
    this.selectionChange.emit(node);
    this.onTouched();
    this.onChange(node);

    if (node.externalUrl) {
      if (typeof window !== 'undefined') {
        window.open(
          node.externalUrl,
          node.openInNewTab === false ? '_self' : '_blank',
          node.openInNewTab === false ? undefined : 'noopener,noreferrer'
        );
      }
      return;
    }

    if (this.navigationType() === 'route') {
      this.navigateToRoute(node.route, node.id);
    } else if (this.showFragments()) {
      this.updateFragment(node.id);
    }
  }

  handleToggleId(id: string) {
    if (this.readonly() || this.isDisabled) return;
    this.expandedNodeIds.update((prev) => {
      const next = new Set(prev);
      const isExpanded = !next.has(id);
      if (isExpanded) next.add(id);
      else next.delete(id);
      this.nodeToggle.emit({ id, expanded: isExpanded });
      return next;
    });
  }

  handleKeyDown(event: KeyboardEvent) {
    if (this.readonly() || this.isDisabled) return;
    const currentId = this.focusedNodeId();
    if (!currentId) return;

    const getVisibleNodes = (nodes: HxTreeNode[], result: HxTreeNode[] = []): HxTreeNode[] => {
      for (const node of nodes) {
        result.push(node);
        if (node.children?.length && this.expandedNodeIds().has(node.id)) {
          getVisibleNodes(node.children, result);
        }
      }
      return result;
    };

    const visible = getVisibleNodes(this.nodes());
    const currentIndex = visible.findIndex((n) => n.id === currentId);
    if (currentIndex === -1) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex < visible.length - 1) this.focusedNodeId.set(visible[currentIndex + 1].id);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex > 0) this.focusedNodeId.set(visible[currentIndex - 1].id);
        break;
      case 'ArrowRight':
        event.preventDefault();
        const node = visible[currentIndex];
        if (node.children?.length) {
          if (!this.expandedNodeIds().has(node.id)) {
            this.handleToggleId(node.id);
          } else {
            this.focusedNodeId.set(node.children[0].id);
          }
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        const curr = visible[currentIndex];
        if (curr.children?.length && this.expandedNodeIds().has(curr.id)) {
          this.handleToggleId(curr.id);
        } else {
          // Find parent logic
          const findParent = (list: HxTreeNode[], childId: string): HxTreeNode | null => {
            for (const p of list) {
              if (p.children?.some((c) => c.id === childId)) return p;
              if (p.children) {
                const found = findParent(p.children, childId);
                if (found) return found;
              }
            }
            return null;
          };
          const parent = findParent(this.nodes(), curr.id);
          if (parent) this.focusedNodeId.set(parent.id);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleNodeSelect(visible[currentIndex]);
        break;
    }
  }

  private getCurrentFragment(): string | null {
    const fragment = this.router?.routerState?.snapshot?.root?.fragment ?? null;
    if (fragment) return fragment;
    if (typeof window === 'undefined') return null;
    return window.location.hash ? window.location.hash.replace(/^#/, '') : null;
  }

  private updateFragment(fragment: string) {
    if (!fragment || !this.showFragments()) return;
    if (this.router) {
      this.router.navigate([], { fragment });
      this.scrollToSection(fragment);
      return;
    }
    this.updateUrl(undefined, fragment);
    this.scrollToSection(fragment);
  }

  private navigateToRoute(route: string | any[] | undefined, fragment: string) {
    if (!route) {
      this.updateFragment(fragment);
      return;
    }
    if (this.router) {
      const commands = Array.isArray(route) ? route : [route];
      const navigationOptions = this.showFragments() ? { fragment } : {};
      this.router.navigate(commands, navigationOptions);
      if (this.showFragments()) {
        this.scrollToSection(fragment);
      }
      return;
    }
    const routePath = Array.isArray(route) ? route.join('/') : route;
    if (this.showFragments()) {
      this.updateUrl(routePath, fragment);
      this.scrollToSection(fragment);
    } else {
      this.updateUrl(routePath, undefined);
    }
  }

  private scrollToSection(sectionId: string) {
    // Logic handled by parent component in this implementation
  }

  private updateUrl(routePath?: string, fragment?: string) {
    if (typeof window === 'undefined') return;
    const base = routePath ?? `${window.location.pathname}${window.location.search}`;
    const hash = fragment ? `#${fragment}` : '';
    window.history.pushState({}, '', `${base}${hash}`);
  }

  private collectNodesMissingRoute(nodes: HxTreeNode[], result: HxTreeNode[] = []): HxTreeNode[] {
    for (const node of nodes) {
      if (!node.route) result.push(node);
      if (node.children?.length) this.collectNodesMissingRoute(node.children, result);
    }
    return result;
  }

  private collectNodesMissingSection(nodes: HxTreeNode[], result: HxTreeNode[] = []): HxTreeNode[] {
    for (const node of nodes) {
      if (!node.id) result.push(node);
      if (node.children?.length) this.collectNodesMissingSection(node.children, result);
    }
    return result;
  }

  private formatMissingLabels(nodes: HxTreeNode[]): string {
    if (nodes.length === 0) return '';
    const labels = nodes.map((n) => n.label).slice(0, 5);
    const suffix = nodes.length > 5 ? ` +${nodes.length - 5} more` : '';
    return `${labels.join(', ')}${suffix}`;
  }
}
